/**
 * Top Navigation Dropdown Menu with GSAP Animations
 * Optimized for performance and maintainability
 */

const topNavigationDropdowns = () => {
  // Animation configuration constants
  const ANIMATION_CONFIG = {
    duration: {
      container: 0.3,
      opacity: {
        fadeIn: 0.3,
        fadeOut: 0.2
      },
      menuItems: 0.25,
      menuItemsHide: 0.15
    },
    stagger: 0.08,
    delay: 0.1,
    ease: "power2.out",
    movement: {
      container: 30,
      menuItems: 0
    }
  };

  // Cache DOM elements
  const menuItems = document.querySelectorAll('#menu-primary-navigation > .menu-item');
  
  // State management
  let activeDropdown = null;

  // Early returns for missing dependencies or elements
  if (typeof gsap === 'undefined') {
    console.error('GSAP is required for top navigation dropdowns. Please ensure GSAP is loaded before this script.');
    return;
  }

  if (!menuItems.length) {
    console.warn('No menu items found for top navigation dropdowns');
    return;
  }

  // Utility functions
  const killAnimations = (elements) => {
    gsap.killTweensOf(elements);
  };

  const setInertState = (element, isInert) => {
    if (isInert) {
      element.setAttribute('inert', '');
    } else {
      element.removeAttribute('inert');
    }
  };

  // Animation functions
  const animateContainer = (subMenu, direction = 'in') => {
    const isShowing = direction === 'in';
    const config = ANIMATION_CONFIG;
    
    const containerAnimation = {
      duration: isShowing ? config.duration.opacity.fadeIn : config.duration.opacity.fadeOut,
      opacity: isShowing ? 1 : 0,
      ease: config.ease
    };

    const positionAnimation = {
      duration: config.duration.container,
      y: isShowing ? 20 : config.movement.container,
      pointerEvents: isShowing ? 'auto' : 'none',
      ease: config.ease
    };

    if (!isShowing) {
      containerAnimation.onComplete = () => setInertState(subMenu, true);
    }

    gsap.to(subMenu, containerAnimation);
    gsap.to(subMenu, positionAnimation);
  };

  const animateMenuItems = (menuItems, direction = 'in') => {
    const isShowing = direction === 'in';
    const config = ANIMATION_CONFIG;

    if (isShowing) {
      gsap.fromTo(menuItems, {
        opacity: 0,
        y: config.movement.menuItems
      }, {
        duration: config.duration.menuItems,
        opacity: 1,
        y: 0,
        ease: config.ease,
        stagger: config.stagger,
        delay: config.delay
      });
    } else {
      gsap.to(menuItems, {
        duration: config.duration.menuItemsHide,
        opacity: 0,
        y: config.movement.menuItems,
        ease: config.ease
      });
    }
  };

  // Core dropdown functions
  const showDropdown = (menuItem) => {
    const subMenu = menuItem.querySelector('.sub-menu');
    if (!subMenu) return;

    // Hide currently active dropdown
    if (activeDropdown && activeDropdown !== menuItem) {
      hideDropdown(activeDropdown);
    }

    // Cache menu items for this dropdown - only direct children (second level)
    const menuItemElements = subMenu.querySelectorAll(':scope > .menu-item');
    
    // Kill existing animations
    killAnimations([subMenu, menuItemElements]);
    
    // Update state
    menuItem.classList.add('dropdown-active');
    activeDropdown = menuItem;
    setInertState(subMenu, false);

    // Animate
    animateContainer(subMenu, 'in');
    animateMenuItems(menuItemElements, 'in');
  };

  const hideDropdown = (menuItem) => {
    const subMenu = menuItem.querySelector('.sub-menu');
    if (!subMenu) return;

    // Cache menu items for this dropdown - only direct children (second level)
    const menuItemElements = subMenu.querySelectorAll(':scope > .menu-item');
    
    // Kill existing animations
    killAnimations([subMenu, menuItemElements]);
    
    // Update state
    menuItem.classList.remove('dropdown-active');
    if (activeDropdown === menuItem) {
      activeDropdown = null;
    }

    // Animate
    animateMenuItems(menuItemElements, 'out');
    animateContainer(subMenu, 'out');
  };

  const hideAllDropdowns = () => {
    if (!activeDropdown) return;
    
    menuItems.forEach(menuItem => {
      if (menuItem.classList.contains('dropdown-active')) {
        hideDropdown(menuItem);
      }
    });
  };

  // Expose hideAllDropdowns globally for scroll handler
  window.topNavigation = window.topNavigation || {};
  window.topNavigation.hideAllDropdowns = hideAllDropdowns;

  // Event handlers
  const handleOutsideClick = (e) => {
    if (!e.target.closest('#menu-primary-navigation')) {
      hideAllDropdowns();
    }
  };

  const handleKeydown = (e) => {
    if (e.key === 'Escape') {
      hideAllDropdowns();
      
      // Return focus to active trigger
      if (activeDropdown) {
        const trigger = activeDropdown.querySelector('a');
        trigger?.focus();
      }
    }
  };

  const createFocusHandler = (menuItem, subMenu) => {
    let focusTimeout;
    
    return {
      onFocus: () => {
        clearTimeout(focusTimeout);
        // Only show dropdown if it's not already active
        if (!menuItem.classList.contains('dropdown-active')) {
          showDropdown(menuItem);
        }
      },
      onBlur: () => {
        clearTimeout(focusTimeout);
        focusTimeout = setTimeout(() => {
          if (!menuItem.contains(document.activeElement)) {
            hideDropdown(menuItem);
          }
        }, 10);
      }
    };
  };

  // Setup third level dropdowns (grandchildren)
  const setupThirdLevelDropdown = (secondLevelItem) => {
    const thirdLevelMenu = secondLevelItem.querySelector('.sub-menu');
    if (!thirdLevelMenu) return;

    const config = ANIMATION_CONFIG;
    let hideTimeout;
    
    // Set initial states for third level - slide up effect
    gsap.set(thirdLevelMenu, {
      opacity: 0,
      y: 30,
      pointerEvents: 'none'
    });

    const thirdLevelItems = thirdLevelMenu.querySelectorAll('.menu-item');
    gsap.set(thirdLevelItems, {
      opacity: 0,
      y: 10
    });

    setInertState(thirdLevelMenu, true);

    const showThirdLevel = () => {
      clearTimeout(hideTimeout);
      killAnimations([thirdLevelMenu, thirdLevelItems]);
      setInertState(thirdLevelMenu, false);
      thirdLevelMenu.classList.add('third-level-active');
      
      // Animate container sliding up
      gsap.to(thirdLevelMenu, {
        duration: config.duration.container,
        opacity: 1,
        y: 0,
        pointerEvents: 'auto',
        ease: config.ease
      });

      // Animate menu items with stagger
      gsap.fromTo(thirdLevelItems, {
        opacity: 0,
        y: 10
      }, {
        duration: config.duration.menuItems,
        opacity: 1,
        y: 0,
        ease: config.ease,
        stagger: config.stagger,
        delay: config.delay
      });
    };

    const hideThirdLevel = () => {
      killAnimations([thirdLevelMenu, thirdLevelItems]);
      
      gsap.to(thirdLevelItems, {
        duration: config.duration.menuItemsHide,
        opacity: 0,
        y: 10,
        ease: config.ease
      });

      gsap.to(thirdLevelMenu, {
        duration: config.duration.opacity.fadeOut,
        opacity: 0,
        y: 30,
        pointerEvents: 'none',
        ease: config.ease,
        onComplete: () => {
          setInertState(thirdLevelMenu, true);
          thirdLevelMenu.classList.remove('third-level-active');
        }
      });
    };

    // Show third level on hover of parent item
    secondLevelItem.addEventListener('mouseenter', () => {
      showThirdLevel();
    });

    // Keep third level open when hovering over it (including the bridge)
    thirdLevelMenu.addEventListener('mouseenter', () => {
      clearTimeout(hideTimeout);
    });

    // Delay hiding when leaving parent item
    secondLevelItem.addEventListener('mouseleave', (e) => {
      // Check if we're moving to the third level menu
      const relatedTarget = e.relatedTarget;
      if (relatedTarget && thirdLevelMenu.contains(relatedTarget)) {
        return;
      }
      
      hideTimeout = setTimeout(() => {
        hideThirdLevel();
      }, 100);
    });

    // Hide when leaving third level menu
    thirdLevelMenu.addEventListener('mouseleave', (e) => {
      // Check if we're moving back to the parent item
      const relatedTarget = e.relatedTarget;
      if (relatedTarget && secondLevelItem.contains(relatedTarget)) {
        return;
      }
      
      hideTimeout = setTimeout(() => {
        hideThirdLevel();
      }, 100);
    });
  };

  // Setup individual dropdown
  const setupDropdown = (menuItem) => {
    const subMenu = menuItem.querySelector('.sub-menu');
    const trigger = menuItem.querySelector('a');
    
    if (!subMenu || !trigger) return;

    // Cache elements - only direct children (second level items)
    const menuItemElements = subMenu.querySelectorAll(':scope > .menu-item');
    const subMenuLinks = subMenu.querySelectorAll('a');

    // Set initial states
    const config = ANIMATION_CONFIG;
    gsap.set(subMenu, {
      opacity: 0,
      y: config.movement.container,
      pointerEvents: 'none'
    });
    
    gsap.set(menuItemElements, {
      opacity: 0,
      y: config.movement.menuItems
    });
    
    setInertState(subMenu, true);

    // Setup third level dropdowns for each second level item
    const secondLevelItems = subMenu.querySelectorAll(':scope > .menu-item');
    secondLevelItems.forEach(secondLevelItem => {
      setupThirdLevelDropdown(secondLevelItem);
    });

    // Create focus handler with closure
    const focusHandler = createFocusHandler(menuItem, subMenu);

    // Mouse events
    menuItem.addEventListener('mouseenter', () => showDropdown(menuItem));
    menuItem.addEventListener('mouseleave', () => hideDropdown(menuItem));

    // Focus events for trigger
    trigger.addEventListener('focus', focusHandler.onFocus);
    trigger.addEventListener('blur', focusHandler.onBlur);

    // Focus events for submenu links
    subMenuLinks.forEach(link => {
      link.addEventListener('focus', focusHandler.onFocus);
      link.addEventListener('blur', focusHandler.onBlur);
    });
  };

  // Initialize dropdowns
  menuItems.forEach(setupDropdown);

  // Global event listeners
  document.addEventListener('click', handleOutsideClick);
  document.addEventListener('keydown', handleKeydown);
};

// Initialize when DOM is ready
if (document.readyState !== "loading") {
  topNavigationDropdowns();
} else {
  document.addEventListener("DOMContentLoaded", topNavigationDropdowns);
}
